import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Check, Loader, Search, X } from 'lucide-react';
import { ingestionService } from '../../../../components/api/ingestionService';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';
import { fieldClass, ghostBtn, primaryBtn } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import type { KnowledgeBaseSource } from '../governedMcpTypes';

type KnowledgeWorkspace = {
  id: string;
  title: string;
  description: string;
};

function rowClass(isLight: boolean, selected: boolean): string {
  if (selected) {
    return isLight
      ? 'w-full text-left px-3 py-2.5 text-sm bg-indigo-50 text-indigo-700'
      : 'w-full text-left px-3 py-2.5 text-sm bg-indigo-500/15 text-indigo-200';
  }
  return isLight
    ? 'w-full text-left px-3 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50'
    : 'w-full text-left px-3 py-2.5 text-sm text-neutral-200 hover:bg-neutral-900';
}

function columnClass(isLight: boolean): string {
  return isLight
    ? 'rounded-xl border border-neutral-200 bg-white overflow-hidden min-h-0 h-52 md:h-full flex flex-col'
    : 'rounded-xl border border-neutral-800 bg-neutral-950 overflow-hidden min-h-0 h-52 md:h-full flex flex-col';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function unwrapRows(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => asRecord(item)).filter((item): item is Record<string, unknown> => Boolean(item));
  }
  const rec = asRecord(raw);
  if (!rec) return [];
  return unwrapRows(rec.data ?? rec.Data ?? rec.items ?? rec.Items ?? rec.result ?? rec.Result);
}

function asText(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function mapWorkspace(raw: Record<string, unknown>): KnowledgeWorkspace | null {
  const id = asText(raw.id ?? raw.Id);
  if (!id) return null;
  return {
    id,
    title: asText(raw.title ?? raw.Title) || id,
    description: asText(raw.description ?? raw.Description),
  };
}

function mapKnowledgeBase(raw: Record<string, unknown>, workspace: KnowledgeWorkspace): KnowledgeBaseSource | null {
  const id = asText(raw.id ?? raw.Id ?? raw.knowledgeBaseId ?? raw.KnowledgeBaseId);
  if (!id) return null;
  return {
    id,
    name: asText(raw.title ?? raw.Title ?? raw.name ?? raw.Name) || id,
    description: asText(raw.description ?? raw.Description),
    documentCount: Number(raw.documentCount ?? raw.DocumentCount ?? 0) || undefined,
    workspaceId: workspace.id,
    workspaceName: workspace.title,
  };
}

export default function KnowledgeBasePickerModal({
  open,
  selectedIds,
  knownBases = [],
  isLight,
  onClose,
  onApply,
}: {
  open: boolean;
  selectedIds: string[];
  knownBases?: KnowledgeBaseSource[];
  isLight: boolean;
  onClose: () => void;
  onApply: (ids: string[], items: KnowledgeBaseSource[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [workspaces, setWorkspaces] = useState<KnowledgeWorkspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState('');
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [basesByWorkspace, setBasesByWorkspace] = useState<Record<string, KnowledgeBaseSource[]>>({});
  const [basesError, setBasesError] = useState<string | null>(null);
  const [basesLoading, setBasesLoading] = useState(false);
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);
  const selectedKey = selectedIds.join(',');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setQuery('');
    setWorkspaceError(null);
    setBasesError(null);
    setDraftIds(selectedKey ? selectedKey.split(',') : []);
    setWorkspacesLoading(true);
    logGovernedMcp('app', 'publish_knowledge_picker_open', {
      selected: selectedKey ? selectedKey.split(',').length : 0,
    });
    void (async () => {
      try {
        const rows = unwrapRows(await ingestionService.loadkbEntitiesList())
          .map(mapWorkspace)
          .filter((item): item is KnowledgeWorkspace => Boolean(item));
        if (cancelled) return;
        setWorkspaces(rows);
        setWorkspaceId((current) => current || rows[0]?.id || '');
        logGovernedMcp('app', 'publish_knowledge_workspaces_ok', { count: rows.length });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load knowledge workspaces';
        if (!cancelled) {
          setWorkspaces([]);
          setWorkspaceError(message);
        }
        logGovernedMcp('error', 'publish_knowledge_workspaces_failed', { message });
      } finally {
        if (!cancelled) setWorkspacesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedKey]);

  const activeWorkspace = workspaces.find((item) => item.id === workspaceId) || null;

  useEffect(() => {
    if (!open || !activeWorkspace) return;
    if (basesByWorkspace[activeWorkspace.id]) return;
    let cancelled = false;
    setBasesLoading(true);
    setBasesError(null);
    logGovernedMcp('app', 'publish_knowledge_bases_load', { workspaceId: activeWorkspace.id });
    void (async () => {
      try {
        const rows = unwrapRows(await knowledgebaseService.getKnowledgeBasesBySourceId(activeWorkspace.id))
          .map((row) => mapKnowledgeBase(row, activeWorkspace))
          .filter((item): item is KnowledgeBaseSource => Boolean(item));
        if (cancelled) return;
        setBasesByWorkspace((prev) => ({ ...prev, [activeWorkspace.id]: rows }));
        logGovernedMcp('app', 'publish_knowledge_bases_ok', {
          workspaceId: activeWorkspace.id,
          count: rows.length,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load knowledge bases';
        if (!cancelled) setBasesError(message);
        logGovernedMcp('error', 'publish_knowledge_bases_failed', {
          workspaceId: activeWorkspace.id,
          message,
        });
      } finally {
        if (!cancelled) setBasesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeWorkspace, basesByWorkspace, open]);

  const visibleWorkspaces = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((item) => `${item.title} ${item.description}`.toLowerCase().includes(q));
  }, [query, workspaces]);

  const workspaceBases = (activeWorkspace && basesByWorkspace[activeWorkspace.id]) || [];
  const visibleBases = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workspaceBases;
    return workspaceBases.filter((item) => `${item.name} ${item.description}`.toLowerCase().includes(q));
  }, [query, workspaceBases]);

  const catalog = useMemo(() => {
    const map = new Map<string, KnowledgeBaseSource>();
    knownBases.forEach((item) => map.set(item.id, item));
    Object.values(basesByWorkspace).forEach((rows) => {
      rows.forEach((item) => map.set(item.id, item));
    });
    return map;
  }, [basesByWorkspace, knownBases]);

  const visibleIds = visibleBases.map((item) => item.id);
  const selectedVisible = visibleIds.filter((id) => draftIds.includes(id)).length;
  const allVisibleOn = visibleBases.length > 0 && selectedVisible === visibleBases.length;

  if (!open) return null;

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const panel = isLight ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-white';
  const rail = isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-950';

  const toggle = (id: string) => {
    setDraftIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close knowledge base picker" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="knowledge-base-picker-title"
        className={`relative flex flex-col w-full max-w-4xl max-h-[min(40rem,calc(100dvh-2rem))] overflow-hidden rounded-2xl shadow-2xl ${panel}`}
      >
        <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-b border-black/5">
          <div>
            <h3 id="knowledge-base-picker-title" className="text-base font-semibold">Select knowledge bases</h3>
            <p className={`text-xs mt-0.5 ${muted}`}>Choose a workspace, then pick one or more knowledge bases.</p>
          </div>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className={`shrink-0 px-5 py-4 border-b ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <div className="relative">
            <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
            <input
              className={`${fieldClass(isLight)} pl-9`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter workspaces or knowledge bases"
            />
          </div>
        </div>
        <div className={`shrink-0 flex items-center justify-between gap-2 px-5 py-2 text-xs ${rail}`}>
          <p className={muted}>
            {selectedVisible} of {visibleBases.length} shown · {draftIds.length} selected
          </p>
          <button
            type="button"
            className={isLight ? 'text-indigo-600' : 'text-indigo-300'}
            disabled={!visibleBases.length}
            onClick={() => {
              setDraftIds((current) =>
                allVisibleOn
                  ? current.filter((id) => !visibleIds.includes(id))
                  : Array.from(new Set([...current, ...visibleIds])),
              );
            }}
          >
            {allVisibleOn ? 'Clear shown' : 'Select shown'}
          </button>
        </div>
        <div className="grid md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] gap-3 p-5 min-h-0 flex-1 overflow-y-auto md:overflow-hidden">
          <section className={columnClass(isLight)}>
            <p className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Workspace</p>
            <div className="overflow-y-auto min-h-0 flex-1">
              {workspacesLoading ? (
                <p className={`flex items-center gap-2 px-3 py-6 text-sm ${muted}`}>
                  <Loader size={14} className="animate-spin" /> Loading workspaces…
                </p>
              ) : null}
              {workspaceError ? <p className="px-3 py-6 text-sm text-red-500">{workspaceError}</p> : null}
              {visibleWorkspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  type="button"
                  className={rowClass(isLight, workspace.id === workspaceId)}
                  onClick={() => {
                    setWorkspaceId(workspace.id);
                    setBasesError(null);
                    logGovernedMcp('app', 'publish_knowledge_workspace_selected', { workspaceId: workspace.id });
                  }}
                >
                  <span className="block truncate font-medium">{workspace.title}</span>
                  {workspace.description ? <span className={`block truncate text-[11px] ${muted}`}>{workspace.description}</span> : null}
                </button>
              ))}
              {!workspacesLoading && !workspaceError && !visibleWorkspaces.length ? (
                <p className={`px-3 py-6 text-sm ${muted}`}>
                  {workspaces.length ? 'No workspaces match this filter.' : 'No knowledge workspaces are available.'}
                </p>
              ) : null}
            </div>
          </section>
          <section className={columnClass(isLight)}>
            <p className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Knowledge bases</p>
            <div className="overflow-y-auto min-h-0 flex-1">
              {basesLoading ? (
                <p className={`flex items-center gap-2 px-3 py-6 text-sm ${muted}`}>
                  <Loader size={14} className="animate-spin" /> Loading knowledge bases…
                </p>
              ) : null}
              {basesError ? <p className="px-3 py-6 text-sm text-red-500">{basesError}</p> : null}
              {visibleBases.map((item) => {
                const checked = draftIds.includes(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={rowClass(isLight, checked)}
                    onClick={() => toggle(item.id)}
                  >
                    <span className="flex items-center gap-2.5">
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded border ${
                          checked
                            ? 'border-indigo-500 bg-indigo-500 text-white'
                            : isLight
                              ? 'border-neutral-300 bg-white'
                              : 'border-neutral-600 bg-neutral-900'
                        }`}
                      >
                        {checked ? <Check size={11} /> : null}
                      </span>
                      <BookOpen size={14} className="shrink-0" />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{item.name}</span>
                        <span className={`block truncate text-[11px] ${muted}`}>
                          {item.documentCount != null ? `${item.documentCount} docs` : 'Knowledge base'}
                          {item.description ? ` · ${item.description}` : ''}
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })}
              {!basesLoading && !basesError && activeWorkspace && !visibleBases.length ? (
                <p className={`px-3 py-6 text-sm ${muted}`}>
                  {workspaceBases.length ? 'No knowledge bases match this filter.' : 'This workspace has no knowledge bases.'}
                </p>
              ) : null}
              {!activeWorkspace && !workspacesLoading ? (
                <p className={`px-3 py-6 text-sm ${muted}`}>Select a knowledge workspace.</p>
              ) : null}
            </div>
          </section>
        </div>
        <div className={`shrink-0 flex items-center justify-end gap-2 px-5 py-4 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtn()}
            onClick={() => {
              const items = draftIds.map((id) => catalog.get(id) || { id, name: id, description: '' });
              onApply(draftIds, items);
              logGovernedMcp('app', 'publish_knowledge_bases_applied', { count: draftIds.length });
            }}
          >
            <Check size={14} /> Use {draftIds.length} knowledge base{draftIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
